/**
 * Platform Detection Utilities
 * Detects whether the user is on mobile or web (desktop) platform
 */

// Breakpoint for mobile detection (matches Tailwind's md breakpoint)
const MOBILE_BREAKPOINT = 768;

/**
 * Detect if the current device is mobile based on screen width
 */
export const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MOBILE_BREAKPOINT;
};

/**
 * Detect if the current device is a desktop/web browser
 */
export const isDesktop = () => {
  return !isMobile();
};

/**
 * Get the current platform type
 * @returns {'mobile' | 'web'}
 */
export const getPlatform = () => {
  return isMobile() ? 'mobile' : 'web';
};

/**
 * Check if user agent suggests a mobile device
 * This is a more reliable check for actual mobile devices
 */
export const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for mobile user agents
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i;
  
  return mobileRegex.test(userAgent.toLowerCase());
};

/**
 * Check if the device is a tablet
 */
export const isTablet = () => {
  if (typeof navigator === 'undefined') return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  
  return /ipad|tablet|playbook|silk/i.test(userAgent) ||
    (userAgent.includes('android') && !userAgent.includes('mobile'));
};

/**
 * Get detailed platform info
 */
export const getPlatformInfo = () => {
  return {
    platform: getPlatform(),
    isMobile: isMobile(),
    isDesktop: isDesktop(),
    isMobileDevice: isMobileDevice(),
    isTablet: isTablet(),
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    screenHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : ''
  };
};

/**
 * Hook to track platform changes on resize
 * Usage: const { platform, isMobile } = usePlatform();
 */
export const createPlatformListener = (callback) => {
  if (typeof window === 'undefined') return () => {};
  
  const handleResize = () => {
    callback(getPlatform());
  };
  
  window.addEventListener('resize', handleResize);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('resize', handleResize);
  };
};

export default {
  isMobile,
  isDesktop,
  getPlatform,
  isMobileDevice,
  isTablet,
  getPlatformInfo,
  createPlatformListener
};
